import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
declare var require: any;
const PageFlip = require('@cdk0507/page-flip').PageFlip;

(pdfjsLib as any).GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/' + (pdfjsLib as any).version + '/pdf.worker.min.js';

interface SearchResult {
  page: number;
  matches: number;
}

@Component({
  selector: 'app-flipbook',
  templateUrl: './flipbook.component.html',
  styleUrls: ['./flipbook.component.scss'],
})
export class FlipbookComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('flipBookContainer') flipBookContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('fullscreenTarget') fullscreenTarget!: ElementRef<HTMLDivElement>;

  // PDF source is provided by the parent (e.g. the download page). No local fallback.
  @Input() pdfSrc = '';
  @Input() documentTitle = '';

  pdfDoc: any = null;
  pageFlip: any = null;
  totalPages = 0;
  currentPage = 1;
  pageImages: string[] = [];
  thumbnails: string[] = [];
  loading = true;
  errorMessage = '';
  showGrid = false;
  showSearch = false;
  showShare = false;
  searchText = '';
  searchResults: SearchResult[] = [];
  activeSearchIndex = 0;
  isSearching = false;
  copied = false;
  currentUrl = window.location.href;
  startAtEnabled = false; // "Start at" checkbox — pins shared link to a chosen page
  startAtPage = 1; // page the shared link opens on when startAtEnabled
  zoom = 1;
  minZoom = 0.8;
  maxZoom = 1.8;
  zoomStep = 0.1;
  flipWidth = 360;
  flipHeight = 430;
  isFlipbookFullscreen = false;

  private viewReady = false;
  private readOpenedAt: number | null = null;
  private lastTrackedPage: number | null = null;
  private readTimeSent = false;

  constructor(private cdr: ChangeDetectorRef, private zone: NgZone) {}

  ngOnInit(): void {
    this.loadPdf();
  }

  ngAfterViewInit(): void {
    // PageFlip is initialized after PDF images are ready.
    this.viewReady = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload when the parent swaps the PDF (skip the very first set handled in ngOnInit).
    if (changes.pdfSrc && !changes.pdfSrc.firstChange) {
      this.sendReadTime();
      this.destroyFlipBook();
      this.loadPdf();
    }
  }

  ngOnDestroy(): void {
    this.sendReadTime();
    this.destroyFlipBook();
  }

  /*
    Centers the first cover page.
    PageFlip keeps cover on the right side internally, so we shift it left by half page width.
    From page 2 onwards, it returns to normal two-page spread position.
  */
  get flipbookTransform(): string {
    if (this.loading || !this.pageFlip) {
      return 'scale(' + this.zoom + ')';
    }
    let pageOffset = 0;
    if (this.currentPage === 1) {
      pageOffset = -(this.flipWidth / 2);
    }
    if (this.currentPage === this.totalPages) {
      pageOffset = this.flipWidth / 2;
    }
    return 'translateX(' + pageOffset + 'px) scale(' + this.zoom + ')';
  }

  async loadPdf(): Promise<void> {
    if (!this.pdfSrc) {
      this.loading = false;
      this.errorMessage = 'No PDF provided.';
      return;
    }
    try {
      this.loading = true;
      this.errorMessage = '';
      const loadingTask = (pdfjsLib as any).getDocument(this.pdfSrc);
      this.pdfDoc = await loadingTask.promise;
      this.totalPages = this.pdfDoc.numPages;
      this.currentPage = 1;
      await this.renderPdfPagesAsImages();
      this.loading = false;
      this.cdr.detectChanges(); // async pdf.js work runs outside Angular's zone — refresh bindings
      setTimeout(() => {
        this.initPageFlip();
      }, 100);
      this.readOpenedAt = Date.now();
      this.readTimeSent = false;
      this.lastTrackedPage = this.currentPage;
      this.track('pdf_open', { total_pages: this.totalPages });
    } catch (error) {
      console.error(error);
      this.loading = false;
      this.totalPages = 0;
      this.errorMessage = 'Unable to load PDF. Please check the PDF path.';
      this.cdr.detectChanges();
    }
  }

  async renderPdfPagesAsImages(): Promise<void> {
    this.pageImages = [];
    this.thumbnails = [];
    for (let pageNumber = 1; pageNumber <= this.totalPages; pageNumber++) {
      const page = await this.pdfDoc.getPage(pageNumber);
      const imageData = await this.renderPageToImage(page, 900);
      const thumbnailData = await this.renderPageToImage(page, 220);
      this.pageImages.push(imageData);
      this.thumbnails.push(thumbnailData);
    }
  }

  async renderPageToImage(page: any, targetWidth: number): Promise<string> {
    const unscaledViewport = page.getViewport({ scale: 1 });
    const scale = targetWidth / unscaledViewport.width;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      return '';
    }
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({
      canvasContext: context,
      viewport,
    }).promise;
    return canvas.toDataURL('image/jpeg', 0.92);
  }

  initPageFlip(): void {
    if (!this.flipBookContainer || this.pageImages.length === 0) {
      return;
    }
    this.destroyFlipBook();
    const container = this.flipBookContainer.nativeElement;
    this.pageFlip = new PageFlip(container, {
      width: this.flipWidth,
      height: this.flipHeight,
      size: 'fixed',
      usePortrait: false,
      showCover: true,
      drawShadow: true,
      flippingTime: 1200,
      startZIndex: 10,
      autoSize: false,
      maxShadowOpacity: 0.55,
      mobileScrollSupport: true,
      useMouseEvents: true,
      swipeDistance: 30,
      clickEventForward: true,
    });

    const pages = container.querySelectorAll('.flip-page');
    this.pageFlip.loadFromHTML(pages);
    this.pageFlip.on('flip', (event: any) => {
      // PageFlip fires outside Angular's zone — run inside so the page number updates
      this.zone.run(() => {
        this.currentPage = Number(event.data) + 1;
        if (this.lastTrackedPage !== this.currentPage) {
          this.lastTrackedPage = this.currentPage;
          this.track('pdf_page_view', { page: this.currentPage });
        }
      });
    });
  }

  destroyFlipBook(): void {
    if (this.pageFlip) {
      try {
        this.pageFlip.destroy();
      } catch (error) {
        console.warn(error);
      }
      this.pageFlip = null;
    }
  }

  nextPage(): void {
    if (!this.pageFlip || this.currentPage >= this.totalPages) {
      return;
    }
    this.pageFlip.flipNext('bottom');
  }

  previousPage(): void {
    if (!this.pageFlip || this.currentPage <= 1) {
      return;
    }
    this.pageFlip.flipPrev('bottom');
  }

  jumpToPage(pageNumber: number): void {
    if (!this.pageFlip || pageNumber < 1 || pageNumber > this.totalPages) {
      return;
    }
    this.showGrid = false;
    this.pageFlip.flip(pageNumber - 1, 'bottom');
  }

  zoomIn(): void {
    this.zoom = Math.min(this.maxZoom, Number((this.zoom + this.zoomStep).toFixed(2)));
  }

  zoomOut(): void {
    this.zoom = Math.max(this.minZoom, Number((this.zoom - this.zoomStep).toFixed(2)));
  }

  resetZoom(): void {
    this.zoom = 1;
  }

  toggleGrid(): void {
    this.showGrid = !this.showGrid;
    this.showSearch = false;
    this.showShare = false;
  }

  toggleSearch(): void {
    this.showSearch = !this.showSearch;
    this.showGrid = false;
    this.showShare = false;
  }

  toggleShare(): void {
    this.showShare = !this.showShare;
    this.showGrid = false;
    if (this.showShare) {
      this.startAtPage = this.currentPage; // default the "Start at" input to current page
    }
  }

  // Download the current PDF (same behaviour as the page's "Download PDF" button)
  downloadPdf(): void {
    if (!this.pdfSrc) {
      return;
    }
    const link = document.createElement('a');
    link.href = this.pdfSrc;
    link.target = '_blank';
    link.rel = 'noopener';
    link.click();
    this.track('pdf_download');
  }

  toggleFullscreen(): void {
    if (!this.fullscreenTarget) {
      return;
    }

    const element = this.fullscreenTarget.nativeElement as any;
    const documentRef = document as any;

    if (!document.fullscreenElement) {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    } else {
      if (documentRef.exitFullscreen) {
        documentRef.exitFullscreen();
      } else if (documentRef.webkitExitFullscreen) {
        documentRef.webkitExitFullscreen();
      } else if (documentRef.mozCancelFullScreen) {
        documentRef.mozCancelFullScreen();
      } else if (documentRef.msExitFullscreen) {
        documentRef.msExitFullscreen();
      }
    }
  }

  async searchPdf(): Promise<void> {
    this.searchResults = [];
    this.activeSearchIndex = 0;
    const keyword = this.searchText.trim().toLowerCase();
    if (!keyword || !this.pdfDoc) {
      return;
    }
    this.isSearching = true;
    try {
      for (let pageNumber = 1; pageNumber <= this.totalPages; pageNumber++) {
        const page = await this.pdfDoc.getPage(pageNumber);
        const textContent = await page.getTextContent();

        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ')
          .toLowerCase();

        const matches = pageText.split(keyword).length - 1;

        if (matches > 0) {
          this.searchResults.push({
            page: pageNumber,
            matches,
          });
        }
      }
      if (this.searchResults.length > 0) {
        this.jumpToPage(this.searchResults[0].page);
      }
      const totalMatches = this.searchResults.reduce((sum, r) => sum + r.matches, 0);
      this.track('pdf_search', { searchText: keyword, matches: totalMatches });
    } catch (error) {
      console.error(error);
    } finally {
      this.isSearching = false;
    }
  }

  goToNextSearchResult(): void {
    if (this.searchResults.length === 0) {
      return;
    }
    this.activeSearchIndex = (this.activeSearchIndex + 1) % this.searchResults.length;
    this.jumpToPage(this.searchResults[this.activeSearchIndex].page);
  }

  goToPreviousSearchResult(): void {
    if (this.searchResults.length === 0) {
      return;
    }
    this.activeSearchIndex = (this.activeSearchIndex - 1 + this.searchResults.length) % this.searchResults.length;
    this.jumpToPage(this.searchResults[this.activeSearchIndex].page);
  }

  isSearchMatch(pageNumber: number): boolean {
    return this.searchResults.some((result) => result.page === pageNumber);
  }

  // Page the shared link should open on
  get sharePageTarget(): number {
    return this.startAtEnabled ? this.startAtPage : this.currentPage;
  }

  get shareUrl(): string {
    return this.currentUrl + '#page=' + this.sharePageTarget;
  }

  async copyShareLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.shareUrl);
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 1500);
    } catch {
      this.copied = false;
    }
    this.track('pdf_share', { channel: 'copy_link' });
  }

  trackShare(channel: string): void {
    this.track('pdf_share', { channel });
  }

  get encodedCurrentUrl(): string {
    return encodeURIComponent(this.shareUrl);
  }

  get progressPercent(): number {
    if (!this.totalPages) {
      return 0;
    }
    return (this.currentPage / this.totalPages) * 100;
  }

  get zoomPercent(): string {
    return Math.round(this.zoom * 100) + '%';
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboard(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') {
      this.nextPage();
    }
    if (event.key === 'ArrowLeft') {
      this.previousPage();
    }
    if (event.key === '+') {
      this.zoomIn();
    }
    if (event.key === '-') {
      this.zoomOut();
    }
    if (event.key === 'Escape') {
      this.showGrid = false;
      this.showSearch = false;
      this.showShare = false;
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.isFlipbookFullscreen = document.fullscreenElement === this.fullscreenTarget?.nativeElement;
  }

  private getDocTitle(): string {
    if (this.documentTitle) {
      return this.documentTitle;
    }
    try {
      const decoded = decodeURIComponent(decodeURIComponent(this.pdfSrc || ''));
      const file = decoded.split('/').pop() || '';
      return file.replace(/\.pdf$/i, '').trim();
    } catch (e) {
      return '';
    }
  }

  private track(eventName: string, extra: { [key: string]: any } = {}): void {
    try {
      const w = window as any;
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({
        event: eventName,
        document_title: this.getDocTitle() || undefined,
        pdf_src: this.pdfSrc || undefined,
        ...extra,
      });
    } catch (error) {
      console.warn('GTM track failed', error);
    }
  }

  private sendReadTime(): void {
    if (this.readTimeSent || !this.readOpenedAt) {
      return;
    }
    const seconds = Math.round((Date.now() - this.readOpenedAt) / 1000);
    this.readTimeSent = true;
    this.track('pdf_read_time', { seconds, exit_page: this.currentPage });
  }
}
